import { Alert, Button, Spinner } from "flowbite-react";
import Input from "../inputs/Input";
import PhoneInput from "../inputs/PhoneInput";
import CPFInput from "../inputs/CPFInput";
import CNPJInput from "../inputs/CNPJInput";
import CurrencyInput from "../inputs/CurrencyInput";
import SelectInput from "../inputs/SelectInput";
import CustomSwitch from "../inputs/CustomSwitch";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useEffect, useMemo, useState, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "../../config/axiosInstance";
import axiosInstance from "axios";
import { ADMIN, CATEGORIES, USERS } from "../../config/apiConfig";
import useCEP from "../../hooks/useCEP";
import { formatToReal, isValidCNPJ, isValidCPF } from "../../utils/utils";
import { getDocType, onlyCnpjChars } from "../../utils/masks";
import { FaCheckCircle, FaRegQuestionCircle } from "react-icons/fa";
import moment from "moment";
import { Tooltip } from "react-tooltip";
import { useApplication } from "../../contexts/ApplicationContext";
import { useNavigate } from "react-router-dom";
import AppBreadcrumb from "../common/AppBreadcrumb";

/* ===========================
 * Helpers
 * =========================== */

const mergeHandlers =
  (...fns) =>
  (e) =>
    fns.filter(Boolean).forEach((fn) => fn(e));

const WIZARD_STEPS = [
  { label: "Cliente", subtitle: "Dados cadastrais" },
  { label: "Usuários", subtitle: "Acesso inicial" },
  { label: "Permissões", subtitle: "Consultas e preços" },
  { label: "Confirmação", subtitle: "Revisão final" },
];

const StepHeader = ({ activeStep, steps }) => {
  const safeStep = Math.min(Math.max(activeStep, 1), steps.length);
  const progress =
    steps.length > 1 ? ((safeStep - 1) / (steps.length - 1)) * 100 : 100;

  return (
    <ol className="grid grid-cols-2 gap-1.5 md:grid-cols-4 md:gap-2">
      {steps.map((item, idx) => {
        const step = idx + 1;
        const done = safeStep > step;
        const current = safeStep === step;

        return (
          <li
            key={item.label}
            className={[
              "rounded-lg border p-2 transition-all",
              current
                ? "border-blue-300 bg-white shadow-sm"
                : done
                  ? "border-blue-200 bg-blue-50/60"
                  : "border-gray-200 bg-white/70",
            ].join(" ")}
          >
            <div className="flex items-start gap-2">
              <span
                className={[
                  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold",
                  current
                    ? "border-blue-500 bg-blue-500 text-white"
                    : done
                      ? "border-blue-400 bg-blue-100 text-blue-700"
                      : "border-gray-300 bg-gray-100 text-gray-500",
                ].join(" ")}
              >
                {done ? "✓" : step}
              </span>
              <div className="min-w-0">
                <div
                  className={[
                    "truncate text-xs font-semibold",
                    current
                      ? "text-blue-700"
                      : done
                        ? "text-blue-800"
                        : "text-gray-600",
                  ].join(" ")}
                >
                  {item.label}
                </div>
                <div className="hidden truncate text-[10px] text-gray-500 md:block">
                  {item.subtitle}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const SectionCard = ({ title, subtitle, children, right }) => (
  <div className="rounded-2xl border bg-white shadow-sm p-4">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const FormRow = ({ children, className = "" }) => (
  <div className={`grid grid-cols-1 gap-4 ${className}`}>{children}</div>
);

const PermissionTabButton = ({
  active,
  onClick,
  title,
  subtitle,
  count,
  tone = "emerald",
  disabled = false,
}) => {
  const toneMap = {
    emerald: {
      active: "from-emerald-500 to-teal-500",
      ring: "ring-emerald-200",
      text: "text-emerald-700",
      badge: "bg-emerald-50 text-emerald-700",
    },
    amber: {
      active: "from-amber-500 to-orange-500",
      ring: "ring-amber-200",
      text: "text-amber-700",
      badge: "bg-amber-50 text-amber-700",
    },
  };
  const palette = toneMap[tone] || toneMap.emerald;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-full text-left rounded-xl border px-4 py-3 transition-all",
        "flex items-center justify-between gap-4",
        disabled
          ? "opacity-50 cursor-not-allowed bg-gray-50"
          : "hover:shadow-sm",
        active
          ? `bg-gradient-to-r ${palette.active} text-white border-transparent shadow-md ring-2 ${palette.ring}`
          : "bg-white border-gray-200",
      ].join(" ")}
    >
      <div className="min-w-0">
        <div
          className={[
            "text-sm md:text-base font-semibold truncate",
            active ? "text-white" : palette.text,
          ].join(" ")}
        >
          {title}
        </div>
        <div
          className={[
            "text-xs truncate",
            active ? "text-white/85" : "text-gray-500",
          ].join(" ")}
        >
          {subtitle}
        </div>
      </div>

      <div
        className={[
          "shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold",
          active ? "bg-white/20 text-white" : palette.badge,
        ].join(" ")}
      >
        {count} ativas
      </div>
    </button>
  );
};

const PermissionAccordionItem = ({
  title,
  badge,
  open,
  onToggle,
  children,
}) => (
  <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm md:text-base font-semibold text-gray-800 truncate">
          {title}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {badge}
        <span className="text-xs text-gray-500">{open ? "^" : "v"}</span>
      </div>
    </button>
    {open && <div className="px-4 pb-4 pt-1">{children}</div>}
  </div>
);

const QueryPermissionRow = ({
  name,
  basePrice,
  fieldPathActive,
  fieldPathPrice,
  control,
  setValue,
}) => {
  const isActive = !!useWatch({ control, name: fieldPathActive });
  const priceVal = Number(useWatch({ control, name: fieldPathPrice }) ?? 0);
  const base = Number(basePrice || 0);
  const isInvalid = isActive && priceVal < base;

  return (
    <div className="border-b border-gray-200 py-3">
      <div className="flex items-center justify-between gap-4">
        <Controller
          name={fieldPathActive}
          control={control}
          render={({ field }) => (
            <CustomSwitch
              id={`switch-${fieldPathActive}`}
              checked={!!field.value}
              onChange={(value) => {
                field.onChange(value);
                if (value && (priceVal === 0 || Number.isNaN(priceVal))) {
                  setValue(fieldPathPrice, base || 0, { shouldDirty: true });
                }
              }}
              label={name}
              subtitle={`Custo base: ${formatToReal(base)}`}
              size="md"
              color="emerald"
            />
          )}
        />

        <div className="min-w-[180px]">
          <CurrencyInput
            name={fieldPathPrice}
            label="Preço"
            control={control}
            disabled={!isActive}
            className={[
              "w-full",
              isInvalid ? "ring-1 ring-red-500 border-red-500 rounded-md" : "",
            ].join(" ")}
          />
          {isInvalid ? (
            <div className="mt-1 text-[10px] text-red-600">
              Preço não pode ser menor que o custo ({formatToReal(base)}).
            </div>
          ) : !isActive ? (
            <div className="mt-1 text-[11px] text-gray-500">
              Ative para editar o preço
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/* ===========================
 * Componente principal
 * =========================== */
const AdminCustomerForm = () => {
  const navigate = useNavigate();
  const { user } = useApplication();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorQuery, setErrorQuery] = useState(false);
  const [permissionTab, setPermissionTab] = useState("main");
  const [openPermissionPanels, setOpenPermissionPanels] = useState({});
  const [detachedFromTable, setDetachedFromTable] = useState(false);
  const [lastSelectedTableId, setLastSelectedTableId] = useState("");
  const [reviewSelections, setReviewSelections] = useState([]);
  const applyingTableValues = useRef(false);

  const today = moment();
  const creationDay = today.clone().subtract(1, "day").date();
  const expirationDay = today.clone().subtract(1, "day").add(10, "days").date();
  const formatDay = (d) => d.toString().padStart(2, "0");

  // OpenCNPJ
  const [openCnpjLoading, setOpenCnpjLoading] = useState(false);
  const [openCnpjError, setOpenCnpjError] = useState(null);
  const openCnpjDebounceRef = useRef(null);
  const lastFetchedCnpjRef = useRef("");

  // OpenCNPJ: fetch + preenchimento
  async function fetchAndFillFromOpenCNPJ(rawCnpj, overwrite = true) {
    const cnpj = onlyCnpjChars(rawCnpj);
    if (!cnpj || cnpj.length !== 14) return;

    setOpenCnpjLoading(true);
    setOpenCnpjError(null);

    try {
      const { data: o } = await axiosInstance.get(
        `https://api.opencnpj.org/${cnpj}`,
      );

      // libera autofills
      userEdited.current.address = false;
      userEdited.current.addressNeighborhood = false;
      userEdited.current.addressCity = false;

      setValue("company.socialReason", o.razao_social || "");
      if (o.email) {
        setValue("company.email", o.email);
        clearErrors("company.email");
      }

      // telefone
      let phone = "";
      try {
        if (Array.isArray(o.telefones) && o.telefones.length) {
          const t = o.telefones[0];
          const ddd = String(t.ddd || "").replace(/\D/g, "");
          const num = String(t.numero || "").replace(/\D/g, "");
          phone = ddd && num ? `(${ddd}) ${num}` : "";
        }
      } catch {}
      if (phone) {
        setValue("company.phoneNumber", phone);
        clearErrors("company.phoneNumber");
      }

      // endereço
      if (o.cep) setValue("company.cep", o.cep);
      if (o.logradouro) setValue("company.address", o.logradouro);
      if (o.numero) setValue("company.addressNumber", o.numero);
      if (o.complemento) setValue("company.addressComplement", o.complemento);
      if (o.bairro) setValue("company.addressNeighborhood", o.bairro);
      if (o.municipio) setValue("company.addressCity", o.municipio);

      lastFetchedCnpjRef.current = cnpj;

      try {
        const firstSocio =
          Array.isArray(o.QSA) && o.QSA.length ? o.QSA[0] : null;
        if (firstSocio?.nome_socio) {
          setValue("company.responsibleName", firstSocio.nome_socio);
          clearErrors("company.responsibleName");
        }
      } catch {}
    } catch (err) {
      setOpenCnpjError("Não foi possível buscar dados no OpenCNPJ.");
    } finally {
      setOpenCnpjLoading(false);
    }
  }

  const defaultValues = {
    company: {
      typeCustomer: "CPF",
      typePayment: "PRE",
      generateAutomaticPayment: 1,
      paymentCreationDay: formatDay(creationDay),
      paymentExpirationDay: formatDay(expirationDay),
      priceSurvey: "10.00",
      priceCV: "10.00",
      // novos campos
      limitUse: "1000", // opcional: deixe vazio ou "0.00"
      assinatura: "0.00",
      dateEndContract: moment().add(1, "year").format("YYYY-MM-DD"),
      hourlyRequestLimit: 30,
    },
    categories: [],
    tableId: "",
  };

  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    setError,
    watch,
    control,
    formState: { errors },
    clearErrors,
    reset,
    trigger,
  } = useForm({ defaultValues, shouldUnregister: false });

  const { setCep, address, loading: loadingCEP } = useCEP();

  /* ===========================
   * Data
   * =========================== */

  const { data: usersList, isLoading: loadingUsers } = useQuery(
    ["usersList"],
    async () => {
      const { data } = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
      return data.data?.data || data.data || [];
    },
  );

  const {
    data: tables,
    isLoading: loadingTables,
    error: errorTables,
  } = useQuery(["tables"], async () => {
    const { data } = await axios.get(`${ADMIN.GET_TABLES}?itemsPerPage=-1`);
    return data.data?.data || [];
  });

  const {
    data: categories,
    isLoading: loadingCategories,
    error: errorCategories,
  } = useQuery(["categories"], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`,
    );
    return data.data?.data || [];
  });

  // pré-seleciona usuário logado como responsável, se vazio
  useEffect(() => {
    if (usersList?.length && !watch("company.responsibleId") && user?.id) {
      setValue("company.responsibleId", user.id);
    }
  }, [usersList, user, watch, setValue]);

  /* ===========================
   * CEP autofill (sem sobrescrever edição)
   * =========================== */
  const userEdited = useRef({
    address: false,
    addressNeighborhood: false,
    addressCity: false,
  });

  const onBlurSearchCEP = () => {
    setCep(getValues("company.cep"));
  };

  useEffect(() => {
    if (!address) return;
    const { logradouro, bairro, localidade } = address || {};

    if (logradouro && !userEdited.current.address) {
      setValue("company.address", logradouro, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (bairro && !userEdited.current.addressNeighborhood) {
      setValue("company.addressNeighborhood", bairro, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (localidade && !userEdited.current.addressCity) {
      setValue("company.addressCity", localidade, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [address, setValue]);

  /* ===========================
   * Dedupe
   * =========================== */
  const onBlurInfo = async (e, type) => {
    try {
      await axios.get(
        `${ADMIN.GET_COMPANY_BY_INFO}/${e.target.value}?type=${type}`,
      );
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Já possui este campo cadastrado!",
      });
    } catch {
      clearErrors(e.target.name);
    }
  };

  const onBlurEmailInfo = async (e) => {
    try {
      await axios.get(`${USERS.GET_USER_BY_EMAIL}/${e.target.value}`);
      clearErrors(e.target.name);
    } catch {
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Já possui este campo cadastrado!",
      });
    }
  };

  /* ===========================
   * Validação CPF/CNPJ
   * =========================== */
  const checkDocument = (e, type) => {
    const value = e.target.value;
    const normalized = onlyCnpjChars(value);
    const isValid =
      getDocType(value) === "CNPJ"
        ? isValidCNPJ(normalized)
        : isValidCPF(normalized);

    if (!isValid) {
      setError(`company.${type}`, {
        type: "manual",
        message: "Documento inválido",
      });
    } else {
      clearErrors(`company.${type}`);
      onBlurInfo(e, type);

      const isCompany = watch("company.typeCustomer") !== "CPF";
      if (isCompany && getDocType(value) === "CNPJ") {
        fetchAndFillFromOpenCNPJ(value);
      }
    }
  };

  /* ===========================
   * Etapas
   * =========================== */
  const handleStep1Submit = () => setActiveStep(2);
  const handleStep2Submit = () => setActiveStep(3);
  const handleStep3Submit = () => {
    const formCategories = getValues("categories") || [];
    setReviewSelections(buildSelectedQueries(formCategories));
    setActiveStep(4);
  };

  const handleFinishSubmit = async (data) => {
    setIsSubmitting(true);
    setErrorQuery(false);
    try {
      const flatQueries = [];
      if (Array.isArray(data.categories)) {
        data.categories.forEach((cat) => {
          if (Array.isArray(cat.queries)) {
            cat.queries.forEach((q) => {
              if (!q) return;
              flatQueries.push({
                ...q,
                price: q.price === "" || q.price == null ? 0 : Number(q.price),
              });
            });
          }
        });
      }
      data.queries = flatQueries.filter((q) => q?.isActive);
      delete data.categories;
      if (!data.tableId || detachedFromTable) {
        data.tableId = null;
      }

      // Converte também company.priceCV / priceSurvey
      if (data.company) {
        if (typeof data.company.priceCV !== "undefined") {
          data.company.priceCV =
            data.company.priceCV === "" || data.company.priceCV == null
              ? null
              : Number(data.company.priceCV);
        }
        if (typeof data.company.priceSurvey !== "undefined") {
          data.company.priceSurvey =
            data.company.priceSurvey === "" || data.company.priceSurvey == null
              ? null
              : Number(data.company.priceSurvey);
        }
        // normaliza limitUse para número se vier preenchido
        if (typeof data.company.limitUse !== "undefined") {
          const v = data.company.limitUse;
          data.company.limitUse =
            v === "" || v == null ? null : Number(String(v).replace(",", "."));
        }
        if (typeof data.company.assinatura !== "undefined") {
          const v = data.company.assinatura;
          data.company.assinatura =
            v === "" || v == null ? null : Number(String(v).replace(",", "."));
        }
      }

      // DEBUG opcional:
      // console.log("PAYLOAD ENVIADO:", JSON.stringify(data, null, 2));

      await axios.post(ADMIN.NEW_CUSTOMER, data);
      queryClient.invalidateQueries(["listCustomers"], { exact: false });
      queryClient.refetchQueries(["listCustomers"], {
        active: true,
        inactive: true,
      });
      setActiveStep(5);
    } catch (error) {
      setErrorQuery(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ===========================
   * UI derivado
   * =========================== */
  const isCPF = watch("company.typeCustomer") === "CPF";
  const isCompany = !isCPF;
  const tableId = watch("tableId");

  const watchedCategories = watch("categories", []);

  const tabActiveCounts = useMemo(() => {
    let main = 0;
    let add = 0;
    (categories || []).forEach((category, idxCat) => {
      (category?.Queries || []).forEach((query, idxQuery) => {
        const isActive =
          watchedCategories?.[idxCat]?.queries?.[idxQuery]?.isActive ?? false;
        if (!isActive) return;
        if (query?.isAdditional) add += 1;
        else main += 1;
      });
    });
    return { main, add };
  }, [categories, watchedCategories]);

  const hasMainQueries = useMemo(
    () =>
      (categories || []).some((category) =>
        category?.Queries?.some((query) => !query.isAdditional),
      ),
    [categories],
  );

  const hasAdditionalQueries = useMemo(
    () =>
      (categories || []).some((category) =>
        category?.Queries?.some((query) => !!query.isAdditional),
      ),
    [categories],
  );

  useEffect(() => {
    if (permissionTab === "main" && !hasMainQueries && hasAdditionalQueries) {
      setPermissionTab("add");
    }
    if (permissionTab === "add" && !hasAdditionalQueries && hasMainQueries) {
      setPermissionTab("main");
    }
  }, [permissionTab, hasMainQueries, hasAdditionalQueries]);

  const { data: tableQueriesPkg, isFetching: fetchingTableQueries } = useQuery(
    ["tableQueries", tableId],
    async () => {
      const currentId = tableId;
      if (!currentId) return null;
      const { data: items } = await axios.get(
        `${ADMIN.GET_TABLE_QUERIES}/${currentId}`,
      );
      return { tableId: currentId, data: items.data };
    },
    { enabled: !!tableId },
  );

  useEffect(() => {
    if (!tableId || !tableQueriesPkg || !categories?.length) return;
    if (tableQueriesPkg.tableId !== tableId) return;

    applyingTableValues.current = true;
    categories.forEach((category, idxCat) => {
      (category?.Queries || []).forEach((query, idxQuery) => {
        const basePath = `categories[${idxCat}].queries[${idxQuery}]`;
        const tableQuery =
          tableQueriesPkg?.data?.find((cq) => cq.QueryId === query.id) || null;

        setValue(`${basePath}.QueryId`, query.id, { shouldDirty: false });
        setValue(`${basePath}.isActive`, !!tableQuery, { shouldDirty: false });
        setValue(`${basePath}.price`, tableQuery?.price ?? query.price, {
          shouldDirty: false,
        });
      });
    });
    setDetachedFromTable(false);

    queueMicrotask(() => {
      applyingTableValues.current = false;
    });
  }, [tableId, tableQueriesPkg, categories, setValue]);

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (!tableId || applyingTableValues.current) return;
      const isQueryPrice = /^categories\[\d+\]\.queries\[\d+\]\.price$/.test(
        name || "",
      );
      const isQueryActive =
        /^categories\[\d+\]\.queries\[\d+\]\.isActive$/.test(name || "");

      if (isQueryPrice || isQueryActive) {
        setDetachedFromTable(true);
        setValue("tableId", "", { shouldDirty: false });
      }
    });

    return () => subscription.unsubscribe();
  }, [tableId, watch, setValue]);

  const hasInvalidQueryPrice = useMemo(() => {
    try {
      return (categories || []).some((category, idxCat) =>
        (category?.Queries || []).some((query, idxQuery) => {
          const isActive = !!watch(
            `categories[${idxCat}].queries[${idxQuery}].isActive`,
          );
          const priceVal = Number(
            watch(`categories[${idxCat}].queries[${idxQuery}].price`) ?? 0,
          );
          return isActive && priceVal < Number(query.price ?? 0);
        }),
      );
    } catch {
      return false;
    }
  }, [categories, watch]);

  const buildSelectedQueries = (formCategories = []) => {
    const items = [];
    (categories || []).forEach((category, idxCat) => {
      const formCat = formCategories?.[idxCat];
      (category?.Queries || []).forEach((query, idxQuery) => {
        const isActive = formCat?.queries?.[idxQuery]?.isActive ?? false;
        if (!isActive) return;
        items.push({
          name: query.name,
          category: category.name,
          isAdditional: !!query.isAdditional,
          price: formCat?.queries?.[idxQuery]?.price,
        });
      });
    });
    return items;
  };

  const selectedQueries = useMemo(
    () => buildSelectedQueries(watchedCategories),
    [categories, watchedCategories],
  );

  const reviewQueries =
    reviewSelections.length > 0 ? reviewSelections : selectedQueries;

  const goToStep2 = async () => {
    // escolha os campos “essenciais” da etapa 1
    const essentials = isCPF
      ? [
          "company.typeCustomer",
          "company.name",
          "company.document",
          "company.email",
          "company.phoneNumber",
          "company.typePayment",
          "company.cep",
          "company.addressCity",
          "company.addressNeighborhood",
          "company.addressNumber",
          "company.responsibleId",
        ]
      : [
          "company.typeCustomer",
          "company.socialReason",
          "company.document",
          "company.responsibleName",
          "company.responsibleCPF",
          "company.position",
          "company.email",
          "company.phoneNumber",
          "company.typePayment",
          "company.cep",
          "company.addressCity",
          "company.addressNeighborhood",
          "company.addressNumber",
          "company.responsibleId",
        ];

    // se quiser, deixe “Endereço” fora para não travar por causa do CEP
    // essentials.push("company.address");

    const ok = await trigger(essentials, { shouldFocus: true });
    if (ok) setActiveStep(2);
  };

  useEffect(() => {
    if (isCPF) {
      setValue("company.hasCV", false, { shouldValidate: true });
      setValue("company.priceCV", "", { shouldValidate: true });
    }
  }, [isCPF, setValue]);

  // Auto-busca OpenCNPJ (debounce)
  useEffect(() => {
    const isCompanyFlag = watch("company.typeCustomer") !== "CPF";
    const raw = watch("company.document");
    const cnpj = onlyCnpjChars(raw);

    if (openCnpjDebounceRef.current) {
      clearTimeout(openCnpjDebounceRef.current);
    }

    if (!isCompanyFlag || cnpj.length !== 14) return;

    openCnpjDebounceRef.current = setTimeout(() => {
      if (lastFetchedCnpjRef.current !== cnpj) {
        if (isValidCNPJ(cnpj)) {
          fetchAndFillFromOpenCNPJ(cnpj, true);
        }
      }
    }, 700);

    return () => {
      if (openCnpjDebounceRef.current) {
        clearTimeout(openCnpjDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("company.document"), watch("company.typeCustomer")]);

  useEffect(() => {
    if (activeStep < 3) return;
    if (!categories?.length) return;
    categories.forEach((category, idxCat) => {
      (category?.Queries || []).forEach((query, idxQuery) => {
        const basePath = `categories[${idxCat}].queries[${idxQuery}]`;
        if (!getValues(`${basePath}.QueryId`)) {
          setValue(`${basePath}.QueryId`, query.id, { shouldDirty: false });
        }
        const currentPrice = getValues(`${basePath}.price`);
        if (typeof currentPrice === "undefined") {
          setValue(`${basePath}.price`, query.price, { shouldDirty: false });
        }
        const currentActive = getValues(`${basePath}.isActive`);
        if (typeof currentActive === "undefined") {
          setValue(`${basePath}.isActive`, false, { shouldDirty: false });
        }
      });
    });
  }, [activeStep, categories, getValues, setValue]);

  const breadcrumbCurrentLabel =
    activeStep > WIZARD_STEPS.length
      ? "Sucesso"
      : WIZARD_STEPS[activeStep - 1]?.label;

  return (
    <div className="space-y-2">
      <AppBreadcrumb
        items={[
          { label: "Clientes", to: "/admin/customers" },
          { label: "Novo cliente", to: "/admin/new-customer" },
        ]}
        currentLabel={breadcrumbCurrentLabel}
        compact
      />
      <div className="bg-white rounded-xl p-2 md:p-5 space-y-6">
        <div className="space-y-3">
          <StepHeader
            activeStep={Math.min(activeStep, WIZARD_STEPS.length)}
            steps={WIZARD_STEPS}
          />
        </div>

        {/* STEP 1: Cliente */}
        {activeStep === 1 && (
          <form
            onSubmit={handleSubmit(handleStep1Submit)}
            className="space-y-6"
          >
            <SectionCard
              title="Dados do cliente"
              subtitle="Preencha as informações cadastrais e de contato."
              right={
                <div className="flex items-center gap-4">
                  {loadingCEP && (
                    <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                      <Spinner size="xs" /> Buscando endereço pelo CEP…
                    </span>
                  )}
                  {openCnpjLoading && (
                    <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                      <Spinner size="xs" /> Buscando dados na receita federal...
                    </span>
                  )}
                  {openCnpjError && (
                    <span className="text-xs text-red-600">
                      {openCnpjError}
                    </span>
                  )}
                </div>
              }
            >
              <FormRow className="lg:grid-cols-3">
                <SelectInput
                  register={register}
                  label="Tipo de cliente"
                  name="company.typeCustomer"
                >
                  <option value="CPF">Pessoa Física</option>
                  <option value="CNPJ">Pessoa Jurídica</option>
                </SelectInput>

                {isCPF ? (
                  <>
                    <div>
                      <Input
                        register={register}
                        name="company.name"
                        label="Nome completo"
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <Controller
                        name="company.document"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Este campo é obrigatório" }}
                        render={({ field, fieldState }) => (
                          <CPFInput
                            name="company.document"
                            label="CPF"
                            required
                            {...field}
                            value={field.value ?? ""}
                            onBlur={mergeHandlers(field.onBlur, (e) =>
                              checkDocument(e, "document"),
                            )}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Controller
                        name="company.document"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Este campo é obrigatório" }}
                        render={({ field, fieldState }) => (
                          <CNPJInput
                            name="company.document"
                            label="CNPJ"
                            required
                            {...field}
                            value={field.value ?? ""}
                            onBlur={mergeHandlers(field.onBlur, (e) =>
                              checkDocument(e, "document"),
                            )}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                    <div>
                      <Input
                        register={register}
                        name="company.socialReason"
                        label="Razão Social"
                        required
                        autoComplete="off"
                      />
                    </div>
                  </>
                )}
              </FormRow>

              {!isCPF && (
                <FormRow className="lg:grid-cols-3">
                  <div>
                    <Input
                      register={register}
                      name="company.responsibleName"
                      label="Nome do responsável"
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Controller
                      name="company.responsibleCPF"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Este campo é obrigatório" }}
                      render={({ field, fieldState }) => (
                        <CPFInput
                          name="company.responsibleCPF"
                          label="CPF do responsável"
                          required
                          {...field}
                          value={field.value ?? ""}
                          onBlur={mergeHandlers(field.onBlur, (e) =>
                            checkDocument(e, "responsibleCPF"),
                          )}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>

                  <SelectInput
                    register={register}
                    label="Cargo"
                    name="company.position"
                  >
                    <option value="SOCIO">Sócio/Administrador</option>
                    <option value="GERENTE">Gerente</option>
                    <option value="ADM">Administrador</option>
                  </SelectInput>
                </FormRow>
              )}

              <FormRow className="lg:grid-cols-3">
                <div>
                  <Input
                    type="email"
                    register={register}
                    name="company.email"
                    label="E-mail *"
                    required
                    autoComplete="off"
                    onBlur={(e) => onBlurInfo(e, "email")}
                  />
                  {errors.company?.email && (
                    <small className="text-xs text-red-500">
                      {errors.company.email.message}
                    </small>
                  )}
                </div>
                <div>
                  <Controller
                    name="company.phoneNumber"
                    control={control}
                    defaultValue=""
                    rules={{ required: "Este campo é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <PhoneInput
                        name="company.phoneNumber"
                        label="Celular *"
                        required
                        {...field}
                        value={field.value ?? ""}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
                <SelectInput
                  register={register}
                  label="Tipo de pagamento"
                  name="company.typePayment"
                >
                  <option value="PRE">Pré-pago</option>
                  <option value="POS">Pós-pago</option>
                </SelectInput>

                {!isCPF && (
                  <div>
                    <Input
                      type="email"
                      register={register}
                      name="company.financeEmail"
                      label="E-mail do financeiro"
                      required
                      autoComplete="off"
                      onBlur={(e) => onBlurInfo(e, "financeEmail")}
                    />
                    {errors.company?.financeEmail && (
                      <small className="text-xs text-red-500">
                        {errors.company.financeEmail.message}
                      </small>
                    )}
                    <div className="text-xs text-gray-500 italic mt-1">
                      Envio da cobrança para este e-mail, caso haja.
                    </div>
                  </div>
                )}
              </FormRow>

              <div>
                <div className="font-semibold pt-3">Endereço do cliente</div>
                <div className="text-gray-500 text-sm mt-1">
                  Preencha o endereço do cliente
                </div>
              </div>
              <FormRow className="lg:grid-cols-3">
                <Input
                  register={register}
                  name="company.cep"
                  label="CEP"
                  required
                  onBlur={onBlurSearchCEP}
                />
                <Input
                  register={register}
                  name="company.address"
                  label="Endereço"
                  required
                  onChange={() => (userEdited.current.address = true)}
                />
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  register={register}
                  name="company.addressNumber"
                  label="Número"
                  required
                />
              </FormRow>

              <FormRow className="lg:grid-cols-3">
                <Input
                  register={register}
                  name="company.addressComplement"
                  label="Complemento"
                />
                <Input
                  register={register}
                  name="company.addressNeighborhood"
                  label="Bairro"
                  required
                  onChange={() =>
                    (userEdited.current.addressNeighborhood = true)
                  }
                />
                <Input
                  register={register}
                  name="company.addressCity"
                  label="Cidade"
                  required
                  onChange={() => (userEdited.current.addressCity = true)}
                />
              </FormRow>

              <div>
                <div className="font-semibold pt-3">Vendedor/Responsável</div>
                <div className="text-gray-500 text-sm mt-1">
                  Usuário Responsável/Vendedor deste cliente
                </div>
              </div>
              <FormRow className="lg:grid-cols-3">
                <SelectInput
                  register={register}
                  label="Usuário responsável (vendedor)"
                  name="company.responsibleId"
                >
                  <option value="">
                    {loadingUsers ? "Carregando usuários..." : "Selecione"}
                  </option>
                  {usersList?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} {u.email ? `— ${u.email}` : ""}
                    </option>
                  ))}
                </SelectInput>
              </FormRow>

              {/* NOVA SEÇÃO: Configurações de segurança */}
              <div>
                <div className="font-semibold pt-3">
                  Configurações de segurança
                </div>
                <div className="text-gray-500 text-sm mt-1">
                  Defina limites de uso mensal e a data de término do contrato
                  deste cliente.
                </div>
              </div>
              <FormRow className="lg:grid-cols-2">
                <div className="">
                  <CurrencyInput
                    register={register}
                    name="company.limitUse"
                    label="Valor máximo de uso (R$)"
                    placeholder="Ex.: R$ 1.000,00"
                    className="w-full"
                    control={control}
                  />
                  <small className="text-xs text-gray-500">
                    Limite máximo permitido para utilização mensal
                  </small>
                </div>
                <div>
                  <Input
                    type="number"
                    register={register}
                    name="company.hourlyRequestLimit"
                    label={
                      <span className="flex items-center gap-2">
                        Qtd. máxima de consultas/hora
                        <FaRegQuestionCircle
                          data-tooltip-id="tt-hourly-limit"
                          className="text-gray-500"
                        />
                      </span>
                    }
                    min={0}
                    max={50}
                    registerOptions={{
                      valueAsNumber: true,
                      min: { value: 0, message: "Mínimo permitido é 0" },
                      max: { value: 50, message: "Máximo permitido é 50" },
                      onChange: (e) => {
                        const num = Number(e.target.value);
                        if (num > 50) e.target.value = 50;
                        if (num < 0) e.target.value = 0;
                      },
                    }}
                    error={errors?.company?.hourlyRequestLimit?.message}
                  />
                  <small className="text-xs text-gray-500">
                    Limite de requisições/hora para proteger contra uso abusivo.
                  </small>
                </div>
                <Input
                  type="date"
                  register={register}
                  name="company.dateEndContract"
                  label="Término do contrato"
                />
                <div>
                  <CurrencyInput
                    register={register}
                    name="company.assinatura"
                    label="Valor da assinatura Mensal (R$)"
                    placeholder="Ex.: R$ 99,00"
                    className="w-full"
                    control={control}
                  />
                  <small className="text-xs text-gray-500">
                    Deixe vazio para não cobrar assinatura.
                  </small>
                </div>
              </FormRow>
            </SectionCard>

            <div className="flex justify-center">
              <Button
                type="button"
                onClick={goToStep2}
                className="shadow md:max-w-60 px-10"
              >
                Próximo
              </Button>
            </div>
          </form>
        )}

        {/* STEP 2: Usuário */}
        {activeStep === 2 && (
          <form
            onSubmit={handleSubmit(handleStep2Submit)}
            className="space-y-6"
          >
            <SectionCard
              title="Usuário administrador"
              subtitle="Crie o primeiro usuário para acessar o painel."
              right={
                <div className="flex items-center gap-2">
                  <Button
                    size="xs"
                    type="button"
                    onClick={() => {
                      const email = watch("company.email");
                      const name =
                        watch("company.responsibleName") ||
                        watch("company.name");
                      const phone = watch("company.phoneNumber");
                      if (email) setValue("customer.email", email);
                      if (name) setValue("customer.name", name);
                      if (phone) setValue("customer.phoneNumber", phone);
                    }}
                  >
                    Reutilizar dados do cliente
                  </Button>
                  <Tooltip
                    id="tt-reuse"
                    content="Preenche nome, e-mail e celular com os dados da etapa anterior."
                  />
                  <FaRegQuestionCircle
                    data-tooltip-id="tt-reuse"
                    className="text-gray-500"
                  />
                </div>
              }
            >
              <FormRow className="lg:grid-cols-2">
                <Input
                  register={register}
                  name="customer.name"
                  label="Nome"
                  required
                  autoComplete="off"
                />
                <div>
                  <Input
                    type="email"
                    register={register}
                    name="customer.email"
                    label="E-mail"
                    required
                    autoComplete="off"
                    onBlur={onBlurEmailInfo}
                  />
                  {errors.customer?.email && (
                    <small className="text-xs text-red-500">
                      {errors.customer.email.message}
                    </small>
                  )}
                </div>
              </FormRow>

              <FormRow className="lg:grid-cols-2">
                <div>
                  <Controller
                    name="customer.phoneNumber"
                    control={control}
                    defaultValue=""
                    rules={{ required: "Este campo é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <PhoneInput
                        name="customer.phoneNumber"
                        label="Celular"
                        required
                        {...field}
                        value={field.value ?? ""}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
                <div>
                  <Input
                    type="password"
                    register={register}
                    name="customer.password"
                    label="Senha"
                    autoComplete="off"
                  />
                  <small className="text-xs text-gray-500">
                    Se vazio, usaremos a senha padrão: 123456
                  </small>
                </div>
              </FormRow>
            </SectionCard>

            <div className="flex justify-center gap-2">
              <Button
                onClick={() => setActiveStep(1)}
                color="gray"
                className="md:max-w-60"
              >
                Voltar
              </Button>
              <Button type="submit" className="md:max-w-60">
                Próximo
              </Button>
            </div>
          </form>
        )}

        {/* STEP 3: Permissões */}
        {activeStep === 3 && (
          <form
            onSubmit={handleSubmit(handleStep3Submit)}
            className="space-y-6"
          >
            <SectionCard
              title="Modelo de tabela"
              subtitle="Opcional: selecione uma tabela para aplicar permissões e preços automaticamente."
              right={
                detachedFromTable && lastSelectedTableId ? (
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => {
                      setValue("tableId", lastSelectedTableId, {
                        shouldDirty: false,
                      });
                      setDetachedFromTable(false);
                    }}
                  >
                    Voltar à tabela
                  </Button>
                ) : null
              }
            >
              {errorTables && (
                <Alert color="failure">Erro ao carregar tabelas.</Alert>
              )}

              <FormRow className="lg:grid-cols-2">
                <SelectInput
                  register={register}
                  label="Tabela pré-definida"
                  name="tableId"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) {
                      setLastSelectedTableId(val);
                    } else {
                      setLastSelectedTableId("");
                    }
                    setDetachedFromTable(false);
                  }}
                >
                  <option value="">
                    {loadingTables
                      ? "Carregando tabelas..."
                      : "Nenhuma selecionada"}
                  </option>
                  {tables?.map((table) => (
                    <option key={table.id} value={table.id}>
                      {table.name}{" "}
                      {table.vcm ? `— ${formatToReal(table.vcm)}` : ""}
                    </option>
                  ))}
                </SelectInput>

                <div className="text-xs text-gray-500">
                  Se você alterar as permissões manualmente, o cliente deixará
                  de seguir a tabela selecionada.
                </div>
              </FormRow>

              {fetchingTableQueries && (
                <div className="inline-flex items-center gap-2 text-xs text-gray-500">
                  <Spinner size="xs" /> Aplicando tabela...
                </div>
              )}

              {detachedFromTable && (
                <Alert color="info">
                  As permissões foram alteradas manualmente. Este cliente não
                  segue mais uma tabela pré-definida.
                </Alert>
              )}
            </SectionCard>

            <SectionCard
              title="Permissões de consulta"
              subtitle="Ative consultas e defina preços para este cliente."
              right={
                loadingCategories ? (
                  <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Carregando consultas...
                  </span>
                ) : null
              }
            >
              {errorCategories && (
                <Alert color="failure">Erro ao carregar categorias.</Alert>
              )}

              {(categories || []).length === 0 && !loadingCategories ? (
                <div className="text-sm text-gray-500">
                  Nenhuma categoria de consulta encontrada.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white via-white to-emerald-50/40 p-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <PermissionTabButton
                        active={permissionTab === "main"}
                        onClick={() => setPermissionTab("main")}
                        title="Consultas principais"
                        subtitle="Essenciais do contrato"
                        count={tabActiveCounts.main}
                        tone="emerald"
                        disabled={!hasMainQueries}
                      />
                      <PermissionTabButton
                        active={permissionTab === "add"}
                        onClick={() => setPermissionTab("add")}
                        title="Consultas adicionais"
                        subtitle="Extras sob demanda"
                        count={tabActiveCounts.add}
                        tone="amber"
                        disabled={!hasAdditionalQueries}
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    {(categories || []).map((category, idxCat) => {
                      const key = `${permissionTab}-${idxCat}`;
                      const isOpen = !!openPermissionPanels[key];
                      const filtered = (category?.Queries || []).filter((q) =>
                        permissionTab === "add"
                          ? !!q.isAdditional
                          : !q.isAdditional,
                      );

                      if (filtered.length === 0) {
                        return null;
                      }

                      const activeCount = filtered.reduce((acc, q) => {
                        const idxQuery = category.Queries.findIndex(
                          (qq) => qq.id === q.id,
                        );
                        const isActive =
                          watchedCategories?.[idxCat]?.queries?.[idxQuery]
                            ?.isActive ?? false;
                        return acc + (isActive ? 1 : 0);
                      }, 0);

                      return (
                        <PermissionAccordionItem
                          key={key}
                          title={category.name}
                          open={isOpen}
                          onToggle={() =>
                            setOpenPermissionPanels((prev) => ({
                              ...prev,
                              [key]: !prev[key],
                            }))
                          }
                          badge={
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              {activeCount} ativas
                            </span>
                          }
                        >
                          <div className="flex flex-col">
                            {filtered.map((query) => {
                              const idxQuery = category.Queries.findIndex(
                                (qq) => qq.id === query.id,
                              );
                              return (
                                <QueryPermissionRow
                                  key={`${category.id}-${query.id}`}
                                  name={query.name}
                                  basePrice={query.price}
                                  fieldPathActive={`categories[${idxCat}].queries[${idxQuery}].isActive`}
                                  fieldPathPrice={`categories[${idxCat}].queries[${idxQuery}].price`}
                                  control={control}
                                  setValue={setValue}
                                />
                              );
                            })}
                          </div>
                        </PermissionAccordionItem>
                      );
                    })}
                  </div>
                </div>
              )}
            </SectionCard>

            {hasInvalidQueryPrice && (
              <Alert color="warning">
                Existem consultas com preço abaixo do custo base. Ajuste antes
                de continuar.
              </Alert>
            )}

            <div className="flex justify-center gap-2">
              <Button
                onClick={() => setActiveStep(2)}
                color="gray"
                className="md:max-w-60"
              >
                Voltar
              </Button>
              <Button
                type="submit"
                className="md:max-w-60"
                disabled={hasInvalidQueryPrice}
                title={
                  hasInvalidQueryPrice
                    ? "Corrija os preços abaixo do custo para continuar"
                    : undefined
                }
              >
                Próximo
              </Button>
            </div>
          </form>
        )}

        {/* STEP 4: Confirmação */}
        {activeStep === 4 && (
          <form
            onSubmit={handleSubmit(handleFinishSubmit)}
            className="space-y-6"
          >
            <SectionCard title="Revisão — cliente">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isCPF ? (
                  <ReviewRow label="Nome" value={watch("company.name")} />
                ) : (
                  <ReviewRow
                    label="Razão Social"
                    value={watch("company.socialReason")}
                  />
                )}
                <ReviewRow label="E-mail" value={watch("company.email")} />
                <ReviewRow
                  label="Celular"
                  value={watch("company.phoneNumber")}
                />
                {!isCPF && (
                  <>
                    <ReviewRow
                      label="Responsável"
                      value={watch("company.responsibleName")}
                    />
                    <ReviewRow
                      label="CPF do responsável"
                      value={watch("company.responsibleCPF")}
                    />
                  </>
                )}
                <ReviewRow label="CEP" value={watch("company.cep")} />
                <ReviewRow label="Endereço" value={watch("company.address")} />
                <ReviewRow
                  label="Número"
                  value={watch("company.addressNumber")}
                />
                <ReviewRow
                  label="Complemento"
                  value={watch("company.addressComplement")}
                />
                <ReviewRow
                  label="Bairro"
                  value={watch("company.addressNeighborhood")}
                />
                <ReviewRow
                  label="Cidade"
                  value={watch("company.addressCity")}
                />
                <ReviewRow
                  label="Tipo de pagamento"
                  value={watch("company.typePayment")}
                />
                {/* Revisão dos novos campos */}
                <ReviewRow
                  label="Valor máximo de uso"
                  value={
                    watch("company.limitUse") !== "" &&
                    watch("company.limitUse") != null
                      ? formatToReal(watch("company.limitUse"))
                      : "—"
                  }
                />
                <ReviewRow
                  label="Valor da assinatura"
                  value={
                    watch("company.assinatura") !== "" &&
                    watch("company.assinatura") != null
                      ? formatToReal(watch("company.assinatura") || 0)
                      : "—"
                  }
                />
                <ReviewRow
                  label="Término do contrato"
                  value={watch("company.dateEndContract")}
                />
              </div>
            </SectionCard>

            <SectionCard title="Revisão — usuário">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ReviewRow label="Nome" value={watch("customer.name")} />
                <ReviewRow label="E-mail" value={watch("customer.email")} />
                <ReviewRow
                  label="Celular"
                  value={watch("customer.phoneNumber")}
                />
              </div>
            </SectionCard>

            <SectionCard title="Revisão — permissões">
              {reviewQueries.length === 0 ? (
                <div className="text-sm text-gray-500">
                  Nenhuma permissão selecionada.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">
                    {reviewQueries.length} permissões ativas
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {reviewQueries.map((item, idx) => (
                      <div
                        key={`${item.name}-${idx}`}
                        className="rounded-lg border border-gray-200 p-2"
                      >
                        <div className="text-sm font-medium text-gray-900">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.category} •{" "}
                          {item.isAdditional ? "Adicional" : "Principal"}
                        </div>
                        <div className="text-xs text-gray-700 mt-1">
                          Preço: {formatToReal(item.price || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>

            <div className="flex justify-center gap-2">
              <Button
                onClick={() => setActiveStep(3)}
                color="gray"
                className="md:max-w-60"
              >
                Voltar
              </Button>
              <Button
                type="submit"
                className="md:max-w-60"
                isProcessing={isSubmitting}
              >
                Finalizar
              </Button>
            </div>

            {errorQuery && (
              <Alert color="failure" className="mt-3">
                Erro ao tentar cadastrar o cliente!
              </Alert>
            )}
          </form>
        )}

        {/* STEP 5: Sucesso */}
        {activeStep === 5 && (
          <div className="relative overflow-hidden rounded-2xl border bg-white shadow-md">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-emerald-100 opacity-70" />
            <svg
              className="pointer-events-none absolute -top-6 -left-6 w-28 h-28 opacity-50"
              viewBox="0 0 100 100"
              fill="none"
            >
              <circle cx="20" cy="20" r="3" fill="#10B981" />
              <rect x="60" y="10" width="6" height="6" rx="1" fill="#34D399" />
              <circle cx="85" cy="30" r="2.5" fill="#A7F3D0" />
              <rect x="12" y="70" width="5" height="5" rx="1" fill="#6EE7B7" />
              <circle cx="42" cy="85" r="2" fill="#10B981" />
            </svg>
            <svg
              className="pointer-events-none absolute -bottom-6 -right-6 w-28 h-28 opacity-50"
              viewBox="0 0 100 100"
              fill="none"
            >
              <circle cx="80" cy="80" r="3" fill="#10B981" />
              <rect x="30" y="84" width="6" height="6" rx="1" fill="#34D399" />
              <circle cx="18" cy="62" r="2.5" fill="#A7F3D0" />
              <rect x="82" y="22" width="5" height="5" rx="1" fill="#6EE7B7" />
              <circle cx="58" cy="12" r="2" fill="#10B981" />
            </svg>

            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="mb-4 inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100">
                <FaCheckCircle className="w-12 h-12 text-emerald-600" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900">
                Cliente cadastrado com sucesso!
              </h3>
              <p className="text-sm text-gray-600 mt-2 max-w-xl">
                Tudo certo por aqui. Você já pode gerenciar permissões, convidar
                usuários ou cadastrar outro cliente.
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl text-left">
                <div className="rounded-xl border bg-white/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Cliente
                  </div>
                  <div className="font-medium text-gray-900">
                    {watch("company.typeCustomer") === "CPF"
                      ? watch("company.name")
                      : watch("company.socialReason")}
                  </div>
                </div>
                <div className="rounded-xl border bg-white/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    E-mail
                  </div>
                  <div className="font-medium text-gray-900 break-words">
                    {watch("company.email")}
                  </div>
                </div>
                <div className="rounded-xl border bg-white/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Celular
                  </div>
                  <div className="font-medium text-gray-900">
                    {watch("company.phoneNumber")}
                  </div>
                </div>
                <div className="rounded-xl border bg-white/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Tipo de pagamento
                  </div>
                  <div className="font-medium text-gray-900">
                    {watch("company.typePayment")}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  className="md:max-w-60"
                  onClick={() => {
                    reset(undefined, { keepValues: false });
                    setPermissionTab("main");
                    setOpenPermissionPanels({});
                    setDetachedFromTable(false);
                    setLastSelectedTableId("");
                    setReviewSelections([]);
                    setActiveStep(1);
                  }}
                >
                  Cadastrar outro
                </Button>
                <Button
                  color="light"
                  className="md:max-w-60"
                  onClick={() => {
                    navigate("/admin/customers");
                  }}
                >
                  Ir para a lista
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* helper para revisão */
const ReviewRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right break-all">
      {value || "—"}
    </span>
  </div>
);

export default AdminCustomerForm;
